import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import SubHeader from "components/common/SubHeader";
import CartItem from "components/cart/CartItem";
import {
    getUserCartList,
    updateUserCartQuantity,
    deleteUserCartItems,
} from "api/cart";
import { useSnackbar } from "contexts/SnackbarContext";
import "assets/scss/pages/cart.scss";
import LoadingSpinner from "components/common/LoadingSpiner";
import CommonModal from "components/common/CommonModal";
import NoData from "components/common/NoData";

const Cart = () => {
    const { showError, showSuccess } = useSnackbar();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(false);
    const [cartList, setCartList] = useState([]);
    const [checkedItems, setCheckedItems] = useState(new Set());
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteTargetIds, setDeleteTargetIds] = useState([]);

    useEffect(() => {
        setIsLoading(true);
        getUserCartList()
            .then((res) => {
                setCartList(res.items);
                const allItemIds = new Set(res.items.map((item) => item.id));
                setCheckedItems(allItemIds);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    // 체크박스 변경 핸들러
    const handleCheckChange = (itemId, isChecked) => {
        setCheckedItems((prev) => {
            const newSet = new Set(prev);
            if (isChecked) {
                newSet.add(itemId);
            } else {
                newSet.delete(itemId);
            }
            return newSet;
        });
    };

    // 전체 선택/해제 핸들러
    const handleAllCheckChange = (e) => {
        if (e.target.checked) {
            const allItemIds = new Set(cartList.map((item) => item.id));
            setCheckedItems(allItemIds);
        } else {
            setCheckedItems(new Set());
        }
    };

    // 수량 업데이트 핸들러
    const handleQuantityUpdate = async (itemId, quantity) => {
        try {
            await updateUserCartQuantity(itemId, quantity);
            const res = await getUserCartList();
            setCartList(res.items);
        } catch (err) {
            console.error("수량 업데이트 실패:", err);
            throw err;
        }
    };

    // 서버 상태를 재조회해 목록/선택 상태를 동기화
    const refreshCartList = async () => {
        const res = await getUserCartList();
        const items = res.items ?? [];
        setCartList(items);

        const remainingIds = new Set(items.map((item) => item.id));
        setCheckedItems(
            (prev) => new Set([...prev].filter((id) => remainingIds.has(id)))
        );
    };

    // 삭제 실행 - 단건 삭제 API를 반복 호출하고, 삭제 후 서버 상태를 재조회한다
    const removeItems = async (targetIds) => {
        if (targetIds.length === 0 || isDeleting) return;

        setIsDeleting(true);
        try {
            const { succeededIds, failedIds } = await deleteUserCartItems(
                targetIds
            );

            // 개별 응답 조합 대신 서버 상태를 다시 읽어 UI를 맞춘다
            await refreshCartList();

            // 부분 성공 상태를 사용자에게 그대로 노출
            if (failedIds.length > 0) {
                showError(
                    succeededIds.length > 0
                        ? `${succeededIds.length}개 삭제 완료, ${failedIds.length}개 삭제에 실패했습니다. 다시 시도해주세요.`
                        : `${failedIds.length}개 상품을 삭제하지 못했습니다. 다시 시도해주세요.`
                );
            } else {
                showSuccess(`${succeededIds.length}개 상품을 삭제했습니다.`);
            }
        } catch (err) {
            console.error("장바구니 삭제 처리 실패:", err);
            showError(
                "장바구니 정보를 불러오지 못했습니다. 잠시 후 다시 시도해주세요."
            );
        } finally {
            setIsDeleting(false);
        }
    };

    // 단건 삭제 (상품 카드의 X 버튼)
    const handleRemove = (id) => {
        removeItems([id]);
    };

    // 선택삭제 버튼 - 확인 모달 오픈
    const handleRemoveSelectedClick = () => {
        if (checkedItems.size === 0) {
            showError("삭제할 상품을 선택해주세요.");
            return;
        }
        setDeleteTargetIds(Array.from(checkedItems));
    };

    // 확인 모달에서 삭제 확정
    const handleConfirmRemoveSelected = () => {
        const targetIds = deleteTargetIds;
        setDeleteTargetIds([]);
        removeItems(targetIds);
    };

    // 주문하기 버튼 핸들러 - CartCheckout 페이지로 이동
    const handlePayment = () => {
        // 선택된 상품이 없으면 에러 메시지 표시
        if (checkedItems.size === 0) {
            showError("주문할 상품을 선택해주세요.");
            return;
        }

        // 선택된 상품 정보 필터링
        const selectedItems = cartList.filter((item) =>
            checkedItems.has(item.id)
        );

        // CartCheckout 페이지로 이동하면서 선택된 상품 정보 전달
        navigate("/cart/checkout", {
            state: {
                selectedItems,
                totalPrice: selectedTotalPrice,
                cartItemIds: Array.from(checkedItems).map((id) => Number(id)),
            },
        });
    };

    // 선택된 상품들의 총금액 계산
    const selectedTotalPrice = useMemo(() => {
        return cartList.reduce((total, item) => {
            if (checkedItems.has(item.id)) {
                const itemPrice = Number(item.item_price);
                const quantity = item.quantity;
                return total + itemPrice * quantity;
            }
            return total;
        }, 0);
    }, [cartList, checkedItems]);

    // 스토어로 이동 - replace 로 현재 /cart 엔트리를 교체한다.
    // push 하면 (스토어 → 상품상세 → 장바구니 → 스토어)로 히스토리가 계속 깊어지고
    // 스토어에서 뒤로가기 시 방금 떠난 장바구니로 되돌아가 뒤로가기 흐름이 꼬인다.
    // replace 는 history idx 가 그대로라 useGoBack 의 APP_ENTRY_IDX 판별(네이티브 closeLayer)도 영향받지 않는다.
    const handleGoToStore = () => {
        navigate("/store/shopping", { replace: true });
    };

    // 가격 포맷팅
    const formatPrice = (price) => {
        return Number(price).toLocaleString("ko-KR");
    };

    const isAllChecked =
        cartList.length > 0 && checkedItems.size === cartList.length;
    const isIndeterminate =
        checkedItems.size > 0 && checkedItems.size < cartList.length;

    if (isLoading) {
        return <LoadingSpinner />;
    }
    return (
        <div className="cart-page">
            <SubHeader
                title="장바구니"
                actionButton={{
                    label: "쇼핑 계속하기",
                    onClick: handleGoToStore,
                }}
            />
            <div className="cart-page-container">
                {cartList && cartList.length > 0 ? (
                    <>
                        <div className="cart-page__select-all">
                            <input
                                type="checkbox"
                                id="select-all"
                                className="cart-page__checkbox"
                                checked={isAllChecked}
                                onChange={handleAllCheckChange}
                                ref={(input) => {
                                    if (input) {
                                        input.indeterminate = isIndeterminate;
                                    }
                                }}
                            />
                            <label
                                htmlFor="select-all"
                                className="cart-page__checkbox-label"
                            >
                                전체선택 ({checkedItems.size}/{cartList.length})
                            </label>
                            <button
                                type="button"
                                className="cart-page__delete-selected-btn"
                                onClick={handleRemoveSelectedClick}
                                disabled={checkedItems.size === 0 || isDeleting}
                            >
                                {isDeleting
                                    ? "삭제 중..."
                                    : `선택삭제 (${checkedItems.size})`}
                            </button>
                        </div>

                        <div className="cart-list-wrap">
                            {cartList &&
                                cartList.map((item) => (
                                    <CartItem
                                        key={item.id}
                                        item={item}
                                        isChecked={checkedItems.has(item.id)}
                                        onCheckChange={handleCheckChange}
                                        onQuantityUpdate={handleQuantityUpdate}
                                        onRemove={(id) => handleRemove(id)}
                                        isRemoveDisabled={isDeleting}
                                    />
                                ))}
                        </div>
                    </>
                ) : (
                    <div className="cart-page__empty">
                        <NoData
                            title="장바구니가 비어있습니다"
                            desc="스토어에서 마음에 드는 상품을 담아보세요."
                            btn={{ name: "스토어 둘러보기", link: "/store/shopping" }}
                        />
                    </div>
                )}
            </div>
            {cartList.length > 0 && (
                <div className="cart-total-summary">
                {/* <div className="cart-page__summary-content">
                    <div className="cart-page__summary-row">
                        <span className="cart-page__summary-label">
                            선택 상품 ({checkedItems.size}개)
                        </span>
                    </div>
                    <div className="cart-page__summary-row cart-page__summary-row--total">
                        <span className="cart-page__summary-total-label">
                            총 결제금액
                        </span>
                        <span className="cart-page__summary-total-value">
                            {formatPrice(selectedTotalPrice)}원
                        </span>
                    </div>
                </div> */}

                    <button
                        type="button"
                        className="cart-page__checkout-btn"
                        disabled={checkedItems.size === 0}
                        onClick={handlePayment}
                    >
                        주문하기
                    </button>
                </div>
            )}

            <CommonModal
                isOpen={deleteTargetIds.length > 0}
                onRequestClose={() => setDeleteTargetIds([])}
                className="cart-delete-modal"
            >
                <>
                    <div className="cart-delete-modal__title">
                        {deleteTargetIds.length === cartList.length
                            ? "장바구니를 모두 비우시겠습니까?"
                            : `선택한 ${deleteTargetIds.length}개 상품을 삭제하시겠습니까?`}
                    </div>
                    <div className="cart-delete-modal__actions">
                        <button
                            type="button"
                            className="cart-delete-modal__btn cart-delete-modal__btn--cancel"
                            onClick={() => setDeleteTargetIds([])}
                        >
                            취소
                        </button>
                        <button
                            type="button"
                            className="cart-delete-modal__btn cart-delete-modal__btn--confirm"
                            onClick={handleConfirmRemoveSelected}
                        >
                            삭제
                        </button>
                    </div>
                </>
            </CommonModal>
        </div>
    );
};

export default Cart;
